import { ArrowLeft } from "lucide-react";
import type React from "react";
import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  type ColumnConfig,
  type ColumnFilters,
  MaximusTableReusable,
} from "@/components/MaximusTableReusable";
import {
  buildApiFilter,
  type Connection,
  type FilterFieldSets,
  type SortInput,
} from "@/components/tables/tableFilters";
import type { ScreenName } from "@/constants/screenNames";
import { useExportToExcel } from "@/hooks/useExportToExcel";
import { usePermissions } from "@/hooks/usePermissions";
import {
  type DefaultColumn,
  useScreenColumnPreferences,
} from "@/hooks/useScreenColumnPreferences";
import { type ExportColumn, resolveExportColumns } from "@/lib/exportColumns";
import { useApp } from "../../contexts/AppContext";
import { Button } from "../ui/button";

/** Every row rendered by a multi-view table needs a stable key. */
export interface MultiViewRow {
  rowKey: string;
}

/** Excel export configuration for a single view. */
export interface MultiViewExportConfig {
  form: string;
  fileName: string;
  /** Grid column key -> backend export column. */
  columns: Record<string, ExportColumn>;
}

/**
 * Columns for a view. Views whose filter dropdowns are populated from distinct
 * server values receive them as a `fieldName -> values` map so they can build
 * their own option labels.
 */
export type MultiViewColumns<TRow> =
  | ColumnConfig<TRow>[]
  | ((distinctValues: Record<string, string[]>) => ColumnConfig<TRow>[]);

/**
 * A single view of a {@link MultiViewTable}: its own columns, backing query,
 * filter semantics and export configuration.
 */
export interface MultiViewDefinition<
  TRow extends MultiViewRow,
  TNode = unknown,
> {
  key: string;
  title: string;
  screenName: ScreenName;
  searchPlaceholder: string;
  columns: MultiViewColumns<TRow>;
  defaultColumns: DefaultColumn[];
  /** Column keys pinned to the left when this view is rendered. */
  pinnedColumns?: string[];
  fetchList: (
    filter: Record<string, unknown> | undefined,
    first: number,
    after: string | undefined,
    sort: SortInput[] | undefined,
  ) => Promise<Connection<TNode>>;
  fetchCount: (filter: Record<string, unknown> | undefined) => Promise<number>;
  mapNode: (node: TNode, cursor: string) => TRow;
  /** Column key -> server sort field. */
  sortFieldMap: Record<string, string>;
  defaultSort?: SortInput[];
  filterFields: FilterFieldSets;
  /** Server field names whose distinct values populate filter dropdowns. */
  distinctValueFields?: string[];
  fetchDistinctValues?: (field: string) => Promise<string[]>;
  /** Constraint merged into every live query while this view is active. */
  horizonFilter?: () => Record<string, unknown>;
  /** Scoping filter applied when this view is entered via a toggle. */
  defaultColumnFilters?: ColumnFilters;
  export?: MultiViewExportConfig;
  /** True when this view may originate a drill-down. */
  drillMember?: boolean;
  getDemoRows?: () => TRow[];
  /** Demo equivalent of {@link fetchDistinctValues}, keyed by field name. */
  getDemoOptions?: () => Record<string, string[]>;
  /** Demo-mode client-side search predicate. */
  demoSearchMatch?: (row: TRow, query: string) => boolean;
}

/**
 * Erase a view's node type so differently-typed views can share one `views`
 * array. The cast is safe because the same definition owns both `fetchList`
 * (which produces the nodes) and `mapNode` (which consumes them).
 */
export function defineMultiView<TRow extends MultiViewRow, TNode>(
  view: MultiViewDefinition<TRow, TNode>,
): MultiViewDefinition<TRow> {
  return {
    ...view,
    mapNode: (node, cursor) => view.mapNode(node as TNode, cursor),
  };
}

/** Target of a drill-down resolved from a double-clicked row. */
export interface DrillTarget {
  toViewKey: string;
  columnFilters: ColumnFilters;
}

/** One step of the browser-style view history. */
export interface ViewHistoryEntry {
  viewKey: string;
  scopingFilters?: ColumnFilters;
}

export interface MultiViewTableHandle {
  /** Navigate to a view, optionally seeded with a scoping filter. */
  setView: (viewKey: string, scopingFilters?: ColumnFilters) => void;
  /** Replace the active view's column filters (visible filters + live query). */
  applyColumnFilters: (filters: ColumnFilters) => void;
  /** Drop every recorded history entry and hide the Back button. */
  clearHistory: () => void;
}

export interface MultiViewTableProps<TRow extends MultiViewRow> {
  views: MultiViewDefinition<TRow>[];
  initialViewKey: string;
  /** Host-rendered view switcher, placed in the table's header actions. */
  renderHeaderActions?: (context: {
    currentViewKey: string;
    setView: (viewKey: string) => void;
  }) => React.ReactNode;
  /**
   * Resolves a row double-click into a drill target. Only views flagged
   * `drillMember` may originate a drill; returning null is a no-op.
   */
  resolveDrill?: (row: TRow, fromViewKey: string) => DrillTarget | null;
  /**
   * Clear ad-hoc search, column filters and sort on every navigation, keeping
   * only the destination's scoping filter.
   */
  resetStateOnViewChange?: boolean;
  /** Change this to clear the view history (data-source, preset, anchor resets). */
  historyResetKey?: string | number;
  /**
   * Per-view filters supplied by the host (e.g. a navigation drill-down seed).
   * They take precedence over that view's retained filters for as long as the
   * host supplies them; changing the object navigates to the seeded view and
   * applies them without recording history.
   */
  entryColumnFilters?: Record<string, ColumnFilters | undefined>;
  /** Notified whenever the user edits the active view's column filters. */
  onColumnFiltersChange?: (filters: ColumnFilters, viewKey: string) => void;
  /** Stable key describing the query anchor; a change refetches live data. */
  horizonKey?: string | null;
  /** Bump to force a live refetch while preserving filters, sort and paging. */
  refreshSignal?: number;
  tableRef?: Ref<MultiViewTableHandle>;
  minTableHeight?: number;
  /** Message shown in place of the table when a query fails. */
  errorMessage?: string;
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Config-driven wrapper around {@link MaximusTableReusable} that renders one of
 * several views over the same table shell.
 *
 * Views are switched two ways, both feeding one browser-style history: the host
 * toggles a view via `renderHeaderActions`, and a drill-down navigates from a
 * double-clicked row via `resolveDrill`. History is only recorded when the table
 * is drill-capable, and the Back button pops one entry at a time.
 */
export function MultiViewTable<TRow extends MultiViewRow>({
  views,
  initialViewKey,
  renderHeaderActions,
  resolveDrill,
  resetStateOnViewChange = false,
  historyResetKey,
  entryColumnFilters,
  onColumnFiltersChange,
  horizonKey,
  refreshSignal,
  tableRef,
  minTableHeight = 533,
  errorMessage = "Failed to load data",
}: MultiViewTableProps<TRow>) {
  const { hasPermission } = usePermissions();
  const { mode } = useApp();
  const isDemo = mode === "demo";

  const viewsByKey = useMemo(() => {
    const map: Record<string, MultiViewDefinition<TRow>> = {};
    for (const view of views) map[view.key] = view;
    return map;
  }, [views]);

  // A view seeded by the host on mount wins over `initialViewKey` so a table
  // opened from a navigation drill-down starts on the seeded view.
  const mountState = useRef(
    (() => {
      const seeded = entryColumnFilters
        ? Object.entries(entryColumnFilters).find(([, filters]) => filters)
        : undefined;
      const viewKey =
        seeded && views.some((view) => view.key === seeded[0])
          ? seeded[0]
          : initialViewKey;
      const view = views.find((candidate) => candidate.key === viewKey);
      const filters = seeded?.[1] ?? view?.defaultColumnFilters;
      return { viewKey, view, filters };
    })(),
  ).current;

  const [activeViewKey, setActiveViewKey] = useState(mountState.viewKey);
  const activeViewKeyRef = useRef(activeViewKey);
  const activeView = viewsByKey[activeViewKey] ?? views[0];
  // Hosts rebuild their view definitions on every render (columns close over
  // formatters), so effects key off the view *key* and read the definition from
  // this ref rather than depending on an unstable object.
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;
  const entryFiltersRef = useRef(entryColumnFilters);
  entryFiltersRef.current = entryColumnFilters;

  const [history, setHistory] = useState<ViewHistoryEntry[]>([]);
  // The filter that defines the current view context (a drill's row-scoped
  // filter or a toggle's default filter) — pushed onto history on navigation.
  const scopingFiltersRef = useRef<ColumnFilters | undefined>(
    mountState.filters,
  );

  const [columnFiltersByView, setColumnFiltersByViewState] = useState<
    Record<string, ColumnFilters | undefined>
  >(() =>
    mountState.filters ? { [mountState.viewKey]: mountState.filters } : {},
  );
  const columnFiltersByViewRef = useRef(columnFiltersByView);

  const [rows, setRows] = useState<TRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Monotonic id so only the most recent row request updates state. This
  // prevents a stale/out-of-order response (e.g. from the previous view) from
  // clobbering the current results when navigating between views.
  const rowsRequestId = useRef(0);
  const errorMessageRef = useRef(errorMessage);
  errorMessageRef.current = errorMessage;

  const [serverSearchTerm, setServerSearchTerm] = useState<string>("");
  const [serverPage, setServerPage] = useState(1);
  const [serverPageSize, setServerPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageCursors, setPageCursors] = useState<Record<number, string>>({});
  const [sortParams, setSortParams] = useState<SortInput[] | undefined>(
    mountState.view?.defaultSort,
  );
  const [columnFilterParams, setColumnFilterParams] = useState<
    Record<string, unknown> | undefined
  >(() =>
    mountState.filters && mountState.view
      ? buildApiFilter(mountState.filters, mountState.view.filterFields)
      : undefined,
  );
  const [distinctValues, setDistinctValues] = useState<
    Record<string, string[]>
  >({});

  const drillCapable =
    Boolean(resolveDrill) && views.some((view) => view.drillMember === true);

  const { visibleColumns: savedVisibleColumns, savePreferences } =
    useScreenColumnPreferences(
      activeView.screenName,
      activeView.defaultColumns,
    );

  const columns = useMemo(
    () =>
      typeof activeView.columns === "function"
        ? activeView.columns(distinctValues)
        : activeView.columns,
    [activeView, distinctValues],
  );

  const setViewColumnFilters = useCallback(
    (viewKey: string, filters: ColumnFilters | undefined) => {
      const next = { ...columnFiltersByViewRef.current, [viewKey]: filters };
      columnFiltersByViewRef.current = next;
      setColumnFiltersByViewState(next);
    },
    [],
  );

  const resetPaging = useCallback(() => {
    setServerPage(1);
    setPageCursors({});
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────────
  const fetchRows = useCallback(
    async (
      view: MultiViewDefinition<TRow>,
      sort: SortInput[] | undefined,
      first: number,
      after: string | undefined,
      filter: Record<string, unknown> | undefined,
    ) => {
      const requestId = ++rowsRequestId.current;
      setIsLoading(true);
      setError(null);
      try {
        const result = await view.fetchList(filter, first, after, sort);
        if (requestId !== rowsRequestId.current) return null;
        setRows(
          result.edges.map((edge) => view.mapNode(edge.node, edge.cursor)),
        );
        return result.pageInfo;
      } catch (err) {
        if (requestId !== rowsRequestId.current) return null;
        console.error("Failed to fetch table rows:", err);
        setError(errorMessageRef.current);
        return null;
      } finally {
        if (requestId === rowsRequestId.current) setIsLoading(false);
      }
    },
    [],
  );

  const queryFilter = useCallback(
    (view: MultiViewDefinition<TRow>) => ({
      ...(view.horizonFilter?.() ?? {}),
      ...columnFilterParams,
      ...(serverSearchTerm.trim() ? { summary: serverSearchTerm.trim() } : {}),
    }),
    [columnFilterParams, serverSearchTerm],
  );

  // Fetch rows whenever query inputs change (live mode only — demo is in-memory)
  useEffect(() => {
    if (isDemo) return;
    const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
    const currentFetchPage = serverPage;
    fetchRows(
      activeView,
      sortParams,
      serverPageSize,
      cursor,
      queryFilter(activeView),
    ).then((pageInfo) => {
      if (pageInfo?.endCursor) {
        setPageCursors((prev) => ({
          ...prev,
          [currentFetchPage + 1]: pageInfo.endCursor as string,
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeViewKey,
    sortParams,
    serverPage,
    serverPageSize,
    columnFilterParams,
    serverSearchTerm,
    horizonKey,
    refreshSignal,
  ]);

  // Fetch total count whenever filters/search change (live mode only)
  useEffect(() => {
    if (isDemo) return;
    activeView
      .fetchCount(queryFilter(activeView))
      .then(setTotalCount)
      .catch((err) => {
        console.error("Failed to fetch table count:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeViewKey,
    columnFilterParams,
    serverSearchTerm,
    horizonKey,
    refreshSignal,
  ]);

  // Distinct dropdown values for the active view's filterable columns
  useEffect(() => {
    const view = activeViewRef.current;
    const fields = view.distinctValueFields;
    if (!fields || fields.length === 0) {
      setDistinctValues({});
      return;
    }
    if (isDemo) {
      setDistinctValues(view.getDemoOptions?.() ?? {});
      return;
    }
    const fetchDistinct = view.fetchDistinctValues;
    if (!fetchDistinct) return;
    Promise.all(fields.map((field) => fetchDistinct(field)))
      .then((results) => {
        const next: Record<string, string[]> = {};
        fields.forEach((field, index) => {
          next[field] = results[index];
        });
        setDistinctValues(next);
      })
      .catch((err) => {
        console.error("Failed to fetch table filter options:", err);
      });
  }, [activeViewKey, isDemo]);

  // ── Navigation ─────────────────────────────────────────────────────────
  const navigate = useCallback(
    (
      toViewKey: string,
      options: {
        scopingFilters?: ColumnFilters;
        recordHistory: boolean;
      },
    ) => {
      const toView = viewsByKey[toViewKey];
      if (!toView) return;

      // Snapshot before the refs move on: the updater below runs during the
      // re-render, by which point they already describe the destination.
      const from: ViewHistoryEntry = {
        viewKey: activeViewKeyRef.current,
        scopingFilters: scopingFiltersRef.current,
      };
      if (options.recordHistory && drillCapable) {
        setHistory((prev) => [...prev, from]);
      }

      const retained = resetStateOnViewChange
        ? undefined
        : columnFiltersByViewRef.current[toViewKey];
      // A host-supplied entry filter outranks whatever the user last had on the
      // destination view: re-entering a view the host has seeded should show the
      // seeded context again.
      const applied =
        options.scopingFilters ??
        entryFiltersRef.current?.[toViewKey] ??
        retained ??
        toView.defaultColumnFilters;

      scopingFiltersRef.current =
        options.scopingFilters ?? toView.defaultColumnFilters;
      activeViewKeyRef.current = toViewKey;
      setActiveViewKey(toViewKey);
      setViewColumnFilters(toViewKey, applied);
      setColumnFilterParams(
        applied ? buildApiFilter(applied, toView.filterFields) : undefined,
      );
      setSortParams(toView.defaultSort);
      setServerSearchTerm("");
      setRows([]);
      resetPaging();
    },
    [
      viewsByKey,
      drillCapable,
      resetStateOnViewChange,
      setViewColumnFilters,
      resetPaging,
    ],
  );

  const setView = useCallback(
    (viewKey: string) => {
      if (viewKey === activeViewKeyRef.current) return;
      navigate(viewKey, { recordHistory: true });
    },
    [navigate],
  );

  const handleRowDoubleClick = useCallback(
    (row: TRow) => {
      if (!resolveDrill || !activeView.drillMember) return;
      const target = resolveDrill(row, activeView.key);
      if (!target) return;
      navigate(target.toViewKey, {
        scopingFilters: target.columnFilters,
        recordHistory: true,
      });
    },
    [resolveDrill, activeView, navigate],
  );

  const handleBack = useCallback(() => {
    const previous = history[history.length - 1];
    if (!previous) return;
    setHistory((prev) => prev.slice(0, -1));
    navigate(previous.viewKey, {
      scopingFilters: previous.scopingFilters,
      recordHistory: false,
    });
  }, [history, navigate]);

  useImperativeHandle(
    tableRef,
    () => ({
      setView: (viewKey: string, scopingFilters?: ColumnFilters) => {
        navigate(viewKey, { scopingFilters, recordHistory: true });
      },
      applyColumnFilters: (filters: ColumnFilters) => {
        const viewKey = activeViewKeyRef.current;
        setViewColumnFilters(viewKey, filters);
        setColumnFilterParams(
          buildApiFilter(filters, viewsByKey[viewKey].filterFields),
        );
        resetPaging();
      },
      clearHistory: () => setHistory([]),
    }),
    [navigate, setViewColumnFilters, viewsByKey, resetPaging],
  );

  // A third-party reset (data-source query cleared, filter preset changed,
  // horizon anchor moved) invalidates the visited trail.
  const appliedResetKey = useRef(historyResetKey);
  useEffect(() => {
    if (appliedResetKey.current === historyResetKey) return;
    appliedResetKey.current = historyResetKey;
    setHistory([]);
  }, [historyResetKey]);

  // Apply a host-supplied entry seed. Tracked by reference so we only navigate
  // when the seed itself changes — otherwise this would fight the user and
  // snap back to the seeded view after every manual toggle.
  const appliedEntryFilters = useRef(entryColumnFilters);
  useEffect(() => {
    if (appliedEntryFilters.current === entryColumnFilters) return;
    appliedEntryFilters.current = entryColumnFilters;
    if (!entryColumnFilters) return;
    const seeded = Object.entries(entryColumnFilters).find(
      ([, filters]) => filters,
    );
    if (!seeded?.[1]) return;
    navigate(seeded[0], {
      scopingFilters: seeded[1],
      recordHistory: false,
    });
  }, [entryColumnFilters, navigate]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (query: string) => {
      setServerSearchTerm(query);
      resetPaging();
    },
    [resetPaging],
  );

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFilters) => {
      const viewKey = activeViewKeyRef.current;
      const view = viewsByKey[viewKey];
      setViewColumnFilters(viewKey, filters);
      setColumnFilterParams(buildApiFilter(filters, view.filterFields));
      resetPaging();
      onColumnFiltersChange?.(filters, viewKey);
    },
    [viewsByKey, setViewColumnFilters, resetPaging, onColumnFiltersChange],
  );

  const handleSortChange = useCallback(
    (column: string | null, direction: "asc" | "desc" | null) => {
      const sortMap = viewsByKey[activeViewKeyRef.current].sortFieldMap;
      if (!column || !direction || !sortMap[column]) {
        setSortParams(undefined);
      } else {
        setSortParams([
          {
            field: sortMap[column],
            order: direction === "asc" ? "Ascending" : "Descending",
          },
        ]);
      }
      resetPaging();
    },
    [viewsByKey, resetPaging],
  );

  // ── Export (always the active view) ────────────────────────────────────
  const exportConfig = activeView.export;
  // Ordered visible column keys reported by the grid when the export is run.
  const orderedVisibleKeysRef = useRef<string[] | null>(null);
  const getExportColumns = useCallback(
    () =>
      exportConfig
        ? resolveExportColumns(
            orderedVisibleKeysRef.current,
            exportConfig.columns,
          )
        : [],
    [exportConfig],
  );
  const getExportFilter = useCallback(
    () => queryFilter(activeView),
    [queryFilter, activeView],
  );
  const { triggerExport } = useExportToExcel({
    form: exportConfig?.form ?? "",
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: exportConfig?.fileName ?? "",
    onFinished: () =>
      toast.success("Export complete", {
        description: "Your file has been downloaded.",
      }),
    onError: (msg: string) =>
      toast.error("Export failed", { description: msg }),
  });

  // ── Columns ────────────────────────────────────────────────────────────
  const defaultVisibleColumns = useMemo(
    () =>
      activeView.defaultColumns
        .filter((c) => c.defaultVisible)
        .sort((a, b) => a.defaultOrder - b.defaultOrder)
        .map((c) => c.key),
    [activeView],
  );

  // Saved column preferences update one render behind the active view, and the
  // table is remounted per view (key={activeViewKey}) so it reads its initial
  // visible columns from them. Without this guard it would briefly receive the
  // previous view's keys, which don't exist in the new view's column set,
  // producing an empty intersection and rows with no cells.
  const initialVisibleColumns = useMemo(() => {
    const validKeys = new Set(columns.map((c) => c.key));
    const filtered = savedVisibleColumns.filter((key) => validKeys.has(key));
    if (filtered.length > 0) return filtered;
    return defaultVisibleColumns;
  }, [columns, savedVisibleColumns, defaultVisibleColumns]);

  // Demo mode sorts client-side, so mirror the view's default sort onto the
  // table's initial sort state.
  const demoInitialSort = useMemo(() => {
    const first = activeView.defaultSort?.[0];
    if (!isDemo || !first) return { column: null, direction: null } as const;
    const column =
      Object.keys(activeView.sortFieldMap).find(
        (key) => activeView.sortFieldMap[key] === first.field,
      ) ?? null;
    return {
      column,
      direction:
        first.order === "Ascending" ? ("asc" as const) : ("desc" as const),
    };
  }, [isDemo, activeView]);

  const demoRows = useMemo(
    () => (isDemo ? (activeView.getDemoRows?.() ?? []) : []),
    [isDemo, activeView],
  );

  const demoSearchMatch = activeView.demoSearchMatch;
  const headerActions = renderHeaderActions?.({
    currentViewKey: activeViewKey,
    setView,
  });

  return (
    <div className="mt-4">
      {error ? (
        <div className="flex items-center justify-center py-12 text-destructive">
          {error}
        </div>
      ) : (
        <div className="flex flex-col">
          <MaximusTableReusable
            key={activeViewKey}
            data={isDemo ? demoRows : rows}
            columns={columns}
            title={activeView.title}
            isLoading={isDemo ? false : isLoading}
            minTableHeight={minTableHeight}
            searchPlaceholder={activeView.searchPlaceholder}
            enableRowSelection={false}
            enableZebraStriping={true}
            showSearch={true}
            showAdvancedFilter={true}
            initialVisibleColumns={initialVisibleColumns}
            defaultVisibleColumns={defaultVisibleColumns}
            initialPinnedLeftColumns={activeView.pinnedColumns}
            initialColumnFilters={columnFiltersByView[activeViewKey]}
            onExportToExcel={
              !isDemo && exportConfig && hasPermission("export_data")
                ? (orderedVisibleKeys) => {
                    orderedVisibleKeysRef.current = orderedVisibleKeys;
                    triggerExport();
                  }
                : undefined
            }
            onColumnConfigSave={(visibleKeys) => {
              const cols = visibleKeys.map((key, idx) => ({
                key,
                isVisible: true,
                order: idx,
              }));
              const hiddenCols = columns
                .map((c) => c.key)
                .filter((k) => !visibleKeys.includes(k))
                .map((key, idx) => ({
                  key,
                  isVisible: false,
                  order: visibleKeys.length + idx,
                }));
              savePreferences([...cols, ...hiddenCols]);
            }}
            initialSortColumn={demoInitialSort.column}
            initialSortDirection={demoInitialSort.direction}
            onSortChange={isDemo ? undefined : handleSortChange}
            onFilterChange={handleColumnFilterChange}
            onRowDoubleClick={
              resolveDrill && activeView.drillMember
                ? handleRowDoubleClick
                : undefined
            }
            pagination={{
              enabled: true,
              defaultPageSize: DEFAULT_PAGE_SIZE,
              pageSizeOptions: [25, 50, 100, 200],
              ...(isDemo
                ? {}
                : {
                    totalItems: totalCount,
                    controlledPage: serverPage,
                    onPageChange: (page: number) => setServerPage(page),
                    onPageSizeChange: (newSize: number) => {
                      setServerPageSize(newSize);
                      resetPaging();
                    },
                  }),
            }}
            onSearchChange={isDemo ? undefined : handleSearchChange}
            onSearch={
              isDemo && demoSearchMatch
                ? (data: TRow[], query: string) =>
                    data.filter((row) =>
                      demoSearchMatch(row, query.toLowerCase()),
                    )
                : undefined
            }
            rowIdAccessor={(row: TRow) => String(row.rowKey)}
            headerLeadingActions={
              drillCapable && history.length > 0 ? (
                <Button variant="outline" size="sm" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              ) : undefined
            }
            headerActions={headerActions}
          />
        </div>
      )}
    </div>
  );
}
